import { useEffect, useState } from 'react'

/**
 * Which band the window is in, read from CSS rather than measured here.
 *
 * The breakpoints are declared once, as custom media, in
 * `styles/breakpoints.css`; `global.css` publishes the resulting band as
 * `--viewport-band` on the root. This reads that. A `matchMedia('(max-width:
 * 34rem)')` here would be a second copy of the number, and the copy is the one
 * that goes stale when the edge moves.
 *
 * Almost nothing should need this. Layout answers width in CSS — a named edge
 * for the window, a container query for a column. This exists for the case
 * where the width changes what the *interface offers* rather than how it looks:
 * a Collection whose view stops being the person's to choose (`useCollectionView`).
 * If a new caller only wants to move something, it wants CSS.
 */
export type ViewportBand = 'handheld' | 'narrow' | 'roomy'

function read(): ViewportBand {
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue('--viewport-band')
    .trim()
  return v === 'handheld' || v === 'narrow' ? v : 'roomy'
}

export function useViewportBand(): ViewportBand {
  const [band, setBand] = useState<ViewportBand>(read)

  useEffect(() => {
    // Coalesced to a frame: a drag across the window fires resize continuously,
    // and the band changes at most twice in the whole sweep.
    let frame = 0
    const onResize = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        setBand(read())
      })
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  return band
}
